import { useEffect, useRef } from 'react';
import type { BrandProfileView, Tenant } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { VERSION_CONFLICT_COPY } from '../../brand/brandCopy';
import { useBrandProfile } from '../../brand/queries';
import { BrandInformationCard } from './BrandInformationCard';
import { GuidelinesCard } from './GuidelinesCard';
import { AudiencesSection, ReadOnlySection } from './ReadOnlySections';
import { useBrandInformationForm, type BrandInformationFormState } from './useBrandInformationForm';
import { VocabularyCard } from './VocabularyCard';
import { WorkspaceDetailsCard } from './WorkspaceDetailsCard';

/** What the page says when the brand cannot be read. */
export const BRAND_LOAD_ERROR_TEXT = "We couldn't load your brand's details. Check your connection and try again.";

/**
 * The Brand information page (spec:165, :170; web.md §6.2), the first Edit Brand Workspace page, in
 * order: Brand Workspace Details, the Brand information card, then Company voice and identity, Brand
 * identity, Brand voice, Vocabulary, Writing guidelines, Audiences and Market. The name, the website,
 * the Vocabulary lists and Writing guidelines are editable by an admin and saved by the page's sticky
 * footer; everything else is text, changed through Brand Setup.
 */
export function BrandInformationPage({ workspace, canEdit }: { workspace: Tenant; canEdit: boolean }) {
  const profile = useBrandProfile(workspace.id);
  // A read that fails after one worked keeps the page as it was (and its changes).
  if (profile.data) return <BrandInformationContent view={profile.data} workspace={workspace} canEdit={canEdit} />;
  if (profile.isError) return <ErrorState message={BRAND_LOAD_ERROR_TEXT} onRetry={() => void profile.refetch()} />;
  return <LoadingState />;
}

function BrandInformationContent({
  view,
  workspace,
  canEdit,
}: {
  view: BrandProfileView;
  workspace: Tenant;
  canEdit: boolean;
}) {
  const form = useBrandInformationForm({ view, tenantId: workspace.id, canEdit });
  return (
    <>
      <WorkspaceDetailsCard form={form} view={view} canEdit={canEdit} />
      <BrandInformationCard view={view} workspace={workspace} canEdit={canEdit} onView={form.adopt} />
      <ReadOnlySection section="companyVoice" view={view} />
      <ReadOnlySection section="brandIdentity" view={view} />
      <ReadOnlySection section="brandVoice" view={view} />
      <VocabularyCard form={form} canEdit={canEdit} />
      <GuidelinesCard form={form} canEdit={canEdit} />
      <AudiencesSection view={view} />
      <ReadOnlySection section="market" view={view} />
      <SaveProblemNotice form={form} />
    </>
  );
}

/**
 * Why the last save did not go through, at the end of the page just above the footer, brought into
 * view when it appears (the footer's Save may be pressed from far up the page). A name conflict shows
 * under the name instead. A version conflict offers Reload: the brand is read again and the page
 * starts over from it, so the changes made here are lost (the sentence says so).
 */
function SaveProblemNotice({ form }: { form: BrandInformationFormState }) {
  const ref = useRef<HTMLDivElement>(null);
  const problem = form.problem;
  const shown = problem !== null && problem.kind !== 'name';

  useEffect(() => {
    if (shown) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [shown, problem]);

  if (!shown) return null;
  return (
    <div
      ref={ref}
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-error-dark/30 dark:bg-error-dark-tint dark:text-error-dark"
    >
      <p className="min-w-0 flex-1">{problem.kind === 'conflict' ? VERSION_CONFLICT_COPY.message : problem.message}</p>
      {problem.kind === 'conflict' && (
        <Button type="button" variant="secondary" size="sm" loading={form.reloading} onClick={() => void form.reload()}>
          {VERSION_CONFLICT_COPY.action}
        </Button>
      )}
    </div>
  );
}
